import { getSharedState } from "./windowBus";

// Small indirection so any module can trigger a lock (on a 401, on
// inactivity, on a manual tap) without importing session.ts directly -
// which would create an import cycle since session.ts orchestrates most
// of the other modules. Backed by windowBus so this also works from React
// components compiled into the separate react-app.js bundle - see
// windowBus.ts for why plain module-level state can't cross that boundary.
// "unauthorized": a live API call got a 401 right now. "session_expired":
// discovered via resume-time validation (SessionManager.validateSession(),
// see sessionManager.ts/AuthGate.tsx) rather than an in-flight request -
// same outcome (lock), kept as a distinct reason purely for the
// [LOCKED] reason=... diagnostic log, so a long-idle recovery is
// distinguishable from an ordinary live 401 without needing to log
// anything sensitive.
export type LockReason = "manual" | "unauthorized" | "inactivity" | "blur" | "hidden" | "pagehide" | "session_expired";
export type LockState = "locked" | "unlocked";

interface LockBusState {
  handler: ((reason: LockReason) => void) | null;
  stateListeners: Set<(state: LockState) => void>;
}

function state(): LockBusState {
  return getSharedState<LockBusState>("__lockBusState", () => ({
    handler: null,
    stateListeners: new Set(),
  }));
}

export function setLockHandler(fn: (reason: LockReason) => void): void {
  state().handler = fn;
}

export function requestLock(reason: LockReason): void {
  state().handler?.(reason);
}

// Multi-subscriber broadcast of the resulting lock/unlock state, so
// React-migrated features can start/stop themselves reactively without
// importing session.ts directly (which still owns the single request
// handler above).
export function onLockStateChange(fn: (state: LockState) => void): () => void {
  state().stateListeners.add(fn);
  return () => state().stateListeners.delete(fn);
}

export function notifyLockStateChange(lockState: LockState): void {
  state().stateListeners.forEach((fn) => fn(lockState));
}
